import type { FollowupStatus } from "./api";

/**
 * Pure postmortem-completeness model (F-007). The four mandatory sections
 * gate the transition to `final`; the two F-022 lessons fields are optional
 * and never block anything. Closing the incident no longer depends on it
 * (LP-030). The server remains the authority; this drives the completeness
 * notes on the Postmortem page and the detail card.
 */
export const MANDATORY_SECTIONS = [
  "cause",
  "affectedComponents",
  "resolution",
  "prevention",
] as const;

export type MandatorySection = (typeof MANDATORY_SECTIONS)[number];

export const OPTIONAL_SECTIONS = [
  "positiveLearnings",
  "earlierDetectable",
] as const;

export type OptionalSection = (typeof OPTIONAL_SECTIONS)[number];

// i18n key maps. Callers resolve these via a variable (t(labelKey)), never via
// a string-literal argument, so the check-i18n gate never sees a partial key;
// every value below exists in de.ts.
export const SECTION_LABEL_KEY: Record<
  MandatorySection | OptionalSection,
  string
> = {
  cause: "postmortem.sections.cause",
  affectedComponents: "postmortem.sections.affectedComponents",
  resolution: "postmortem.sections.resolution",
  prevention: "postmortem.sections.prevention",
  positiveLearnings: "postmortem.sections.positiveLearnings",
  earlierDetectable: "postmortem.sections.earlierDetectable",
};

/**
 * Which mandatory sections are still empty (whitespace-only counts as empty,
 * mirroring the backend's F-007 check). Accepts any partial value bag so it
 * works for both the live form values and a saved PostmortemResponse.
 */
export function missingMandatorySections(
  values: Partial<Record<MandatorySection, string | null | undefined>>,
): MandatorySection[] {
  return MANDATORY_SECTIONS.filter(
    (section) => (values[section] ?? "").trim().length === 0,
  );
}

/** Display order of the follow-up task lifecycle (F-008). */
export const FOLLOWUP_STATUSES: readonly FollowupStatus[] = [
  "offen",
  "in_arbeit",
  "erledigt",
  "verworfen",
];

export const FOLLOWUP_STATUS_LABEL_KEY: Record<FollowupStatus, string> = {
  offen: "postmortem.followupStatus.offen",
  in_arbeit: "postmortem.followupStatus.in_arbeit",
  erledigt: "postmortem.followupStatus.erledigt",
  verworfen: "postmortem.followupStatus.verworfen",
};

/**
 * RFC-7807 problem `title` carries the backend i18n key (api problem builder);
 * this maps the postmortem-specific ones to SPA catalog keys so the toast is
 * localized even when the server `detail` is absent. Unknown titles fall back
 * to the generic notifyApiError handling.
 */
export const POSTMORTEM_PROBLEM_KEY: Record<string, string> = {
  "error.postmortem_status_invalid": "errors.postmortemStatusInvalid",
  "error.postmortem_incomplete_for_final": "errors.postmortemIncompleteForFinal",
};

/**
 * Is this postmortem a draft (F-007)? AC-008 asks for it to be marked visibly
 * incomplete, so this is a display predicate, not a gate - the server decides
 * what may be saved.
 */
export function isDraft(
  postmortem: { state?: string } | null | undefined,
): boolean {
  return postmortem?.state === "entwurf";
}
